import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, InputAdornment, IconButton, Link, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { FormProvider, Iconify, RHFTextField } from '@app/components';
import { Strings } from '@app/constants';
import { SignUpSchema } from '../validators';
import { typography } from '@app/theme/typography';
import { SvgColor } from '@app/components/svg-color';
import { PATH_AUTH } from '@app/routes/paths';
import { useRequestState } from '@app/hooks';
import { signUp } from '../slice';
import type { ISignUpSchema } from '../types';
import { selectSignUpState } from '../selectors';

// --------------------------------------------

const SignUpForm = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigation = useNavigate();

  const { data } = useRequestState({
    stateSelector: selectSignUpState,
    errorShown: true,
    successMessageShown: true,
    onSuccess: () => navigation(PATH_AUTH.login),
  });

  const [showPassword, setShowPassword] = useState(false);

  const onClickEyeIcon = () => {
    setShowPassword(!showPassword);
  };

  const methods = useForm<ISignUpSchema>({
    resolver: yupResolver(SignUpSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
    },
  });

  const onSubmit = (form: ISignUpSchema) => {
    dispatch(signUp(form));
  };

  return (
    <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
      <Stack>
        <Typography
          fontFamily={typography.fontFamily}
          fontSize={{ xs: '12px', md: '15px' }}
          fontWeight={typography.fontWeightRegular}
        >
          {Strings.field.fullName}
        </Typography>
        <RHFTextField
          data-testId="login.name"
          name="name"
          type="name"
          fullWidth
          size="small"
          sx={{ marginBottom: 2, backgroundColor: theme.palette.grey[50], borderRadius: '10px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgColor fontSize={'10px'} src={`/assets/icons/ic_profile.svg`} />
              </InputAdornment>
            ),
          }}
        />
        <Typography
          fontFamily={typography.fontFamily}
          fontSize={{ xs: '12px', md: '15px' }}
          fontWeight={typography.fontWeightRegular}
        >
          {Strings.field.emailAddress}
        </Typography>
        <RHFTextField
          data-testId="login.email"
          name="email"
          type="email"
          fullWidth
          size="small"
          sx={{ marginBottom: 2, backgroundColor: theme.palette.grey[50], borderRadius: '10px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgColor fontSize={'10px'} src={`/assets/icons/ic_email.svg`} />
              </InputAdornment>
            ),
          }}
        />
        <Typography
          fontFamily={typography.fontFamily}
          fontSize={{ xs: '12px', md: '15px' }}
          fontWeight={typography.fontWeightRegular}
        >
          {Strings.field.password}
        </Typography>
        <RHFTextField
          data-testId="login.password"
          name="password"
          fullWidth
          size="small"
          sx={{ marginBottom: 2, backgroundColor: theme.palette.grey[50], borderRadius: '10px' }}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgColor fontSize={'10px'} src={`/assets/icons/ic_password.svg`} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onClickEyeIcon}>
                  {showPassword ? <Iconify icon="mdi:eye" /> : <Iconify icon="mdi:eye-off" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <LoadingButton
          data-testId="login.submit"
          size="large"
          variant="contained"
          type="submit"
          // loading={loading}
          sx={{
            marginTop: { xs: 5, md: 10 },
            borderRadius: '50px',
          }}
        >
          {Strings.button.signUp}
        </LoadingButton>
        <Typography variant="subtitle2" alignSelf={'center'} mt={5}>
          {Strings.common.haveAccount}
          <Link
            variant="body2"
            color="inherit"
            href={PATH_AUTH.login}
            sx={{ color: theme.palette.primary.dark, fontWeight: 'bold', ml: 0.5 }}
          >
            {Strings.auth.logIn}
          </Link>
        </Typography>
      </Stack>
    </FormProvider>
  );
};

export { SignUpForm };
